import { useState, useEffect, useRef } from 'react';
import type { ChangeEvent, MouseEvent as ReactMouseEvent } from 'react';
import styles from './SelectTimeRange.module.css';

const generateTimeOptions = () => {
    const times: Array<{ value: number; label: string }> = [];
    for (let hour = 0; hour < 24; hour++) {
        for (let minute = 0; minute < 60; minute += 15) {
            times.push({
                value: hour * 4 + minute / 15,
                label: `${String(hour)}.${String(minute).padStart(2, '0')}`
            });
        }
    }
    return times;
};

const formatTime = (value: number): string => {
    const hour = Math.floor(value / 4);
    const minute = (value % 4) * 15;
    return `${String(hour)}.${String(minute).padStart(2, '0')}`;
};

interface SelectTimeRangeProps {
    onChange?: (range: { start: string; end: string }) => void;
    timesList?: readonly string[];
    /** Slot-Index (0–95), Default 8:00 Uhr. */
    initialStart?: number;
    /** Slot-Index (0–95), Default 19:00 Uhr. */
    initialEnd?: number;
}

export default function SelectTimeRange({ onChange, timesList, initialStart = 32, initialEnd = 76 }: SelectTimeRangeProps) {
    const [startValue, setStartValue] = useState(initialStart);
    const [endValue, setEndValue] = useState(initialEnd);
    const [dragStartValue, setDragStartValue] = useState(initialStart);
    const [dragEndValue, setDragEndValue] = useState(initialEnd);
    const timeOptions = generateTimeOptions();
    const [tooltipPosition, setTooltipPosition] = useState(0);
    const [endTooltipPosition, setEndTooltipPosition] = useState(0);
    const sliderRef = useRef<HTMLDivElement>(null);
    const endSliderRef = useRef<HTMLDivElement>(null);
    const [isStartActive, setIsStartActive] = useState(false);
    const [isEndActive, setIsEndActive] = useState(false);



    const handleSliderChange = (e: ChangeEvent<HTMLInputElement>) => {
        const newValue = parseInt(e.target.value);
        const isStartSlider = e.target.name === 'start';

        if (isStartSlider) {
            if (newValue < dragEndValue) {
                setDragStartValue(newValue);
            }
        } else {
            if (newValue > dragStartValue) {
                setDragEndValue(newValue);
            }
        }
    };

    const handleSliderMouseUp = (e: { target: { name: string } }) => {
        const isStartSlider = e.target.name === 'start';
        if (isStartSlider) {
            setStartValue(dragStartValue);
        } else {
            setEndValue(dragEndValue);
        }
    };

    const updateTooltipPosition = (e: ReactMouseEvent<HTMLInputElement>, isEndSlider = false) => {
        const ref = isEndSlider ? endSliderRef : sliderRef;
        if (ref.current) {
            const rect = ref.current.getBoundingClientRect();
            const position = ((e.clientX - rect.left) / rect.width) * 100;
            const clampedPosition = Math.max(0, Math.min(100, position));

            if (isEndSlider) {
                setEndTooltipPosition(clampedPosition);
            } else {
                setTooltipPosition(clampedPosition);
            }
        }
    };

    const handleTouchStart = (isEndSlider: boolean) => {
        if (isEndSlider) {
            setIsEndActive(true);
        } else {
            setIsStartActive(true);
        }
    };

    const handleTouchEnd = (isEndSlider: boolean) => {
        if (isEndSlider) {
            setIsEndActive(false);
        } else {
            setIsStartActive(false);
        }
        handleSliderMouseUp({ target: { name: isEndSlider ? 'end' : 'start' } });
    };

    // Aktualisiere die temporären Werte wenn sich die echten Werte ändern
    useEffect(() => {
        setDragStartValue(startValue);
        setDragEndValue(endValue);
    }, [startValue, endValue]);

    useEffect(() => {
        if (onChange) {
            onChange({
                start: formatTime(startValue),
                end: formatTime(endValue)
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startValue, endValue]);

    return (
        <div className={styles.timeRangeContainer}>
            <p style={{fontWeight: 'bold'}}>Zeit-Anzeige eingrenzen</p>
            <div className={styles.sliderContainer}>
                <div className={styles.sliderTrack}>
                    <div
                        className={styles.sliderRange}
                        style={{
                            left: `${(dragStartValue / 95) * 100}%`,
                            width: `${((dragEndValue - dragStartValue) / 95) * 100}%`
                        }}
                    />
                </div>
                <div className={styles.sliderTooltip} ref={sliderRef}>
                    <input
                        type="range"
                        name="start"
                        min="0"
                        max="95"
                        value={dragStartValue}
                        onChange={handleSliderChange}
                        onMouseMove={(e) => updateTooltipPosition(e, false)}
                        onMouseDown={() => setIsStartActive(true)}
                        onMouseUp={() => {
                            setIsStartActive(false);
                            handleSliderMouseUp({ target: { name: 'start' } });
                        }}
                        onTouchStart={() => handleTouchStart(false)}
                        onTouchEnd={() => handleTouchEnd(false)}
                        className={styles.slider}
                    />
                    <div
                        className={`${styles.sliderTooltipText} ${isStartActive ? styles.active : ''}`}
                        style={{ left: `${tooltipPosition}%` }}
                    >
                        {formatTime(dragStartValue)}
                    </div>
                </div>
                <div className={styles.sliderTooltip} ref={endSliderRef}>
                    <input
                        type="range"
                        name="end"
                        min="0"
                        max="95"
                        value={dragEndValue}
                        onChange={handleSliderChange}
                        onMouseMove={(e) => updateTooltipPosition(e, true)}
                        onMouseDown={() => setIsEndActive(true)}
                        onMouseUp={() => {
                            setIsEndActive(false);
                            handleSliderMouseUp({ target: { name: 'end' } });
                        }}
                        onTouchStart={() => handleTouchStart(true)}
                        onTouchEnd={() => handleTouchEnd(true)}
                        className={styles.slider}
                    />
                    <div
                        className={`${styles.sliderTooltipText} ${isEndActive ? styles.active : ''}`}
                        style={{ left: `${endTooltipPosition}%` }}
                    >
                        {formatTime(dragEndValue)}
                    </div>
                </div>
            </div>

            <div className={styles.timeDisplay}>
                <div className={styles.timeValue}>
                    <label>Von:</label>
                    <select
                        value={startValue}
                        onChange={(e) => {
                            const newValue = parseInt(e.target.value);
                            setStartValue(newValue);
                            setDragStartValue(newValue);
                        }}
                        className={styles.timeSelect}
                    >
                        {timeOptions.map(time => (
                            <option
                                key={time.value}
                                value={time.value}
                                disabled={time.value >= endValue}
                            >
                                {time.label}
                            </option>
                        ))}
                    </select>
                </div>
                <div className={styles.timeValue}>
                    <label>Bis:</label>
                    <select
                        value={endValue}
                        onChange={(e) => {
                            const newValue = parseInt(e.target.value);
                            setEndValue(newValue);
                            setDragEndValue(newValue);
                        }}
                        className={styles.timeSelect}
                    >
                        {timeOptions.map(time => (
                            <option
                                key={time.value}
                                value={time.value}
                                disabled={time.value <= startValue}
                            >
                                {time.label}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        </div>
    );
}
